/**
 * Phrase selection (04-flows principle 6, F4 "Phrase selection", F8 span header; D11).
 *
 * The pure "phrase-selection reducer": tap-to-extend, ◀ ▶ nudge, and swipe all reduce to a
 * `PhraseSelection` — a span from one WORD token to another on the same or an ADJACENT line,
 * capped at `MAX_PHRASE_WORDS` word tokens. Everything here is derived from `tokenize()` (the same
 * tokens `Transcript.tsx` renders as `.nn-w`), so token indices (`tokIdx`, incl. whitespace tokens)
 * and char offsets line up with the DOM. No React, no DOM — vitest in node.
 *
 * Words = tokens with `word !== ''`; punctuation-only and whitespace tokens are never selected
 * and never count. Line adjacency is by array position in `lines` (ordered by idx).
 */
import type { Line } from '@nelnela/contracts';
import { WORD_TRIM, tokenize, type Token } from './text';

/** ~8 tokens (principle 6) — the cap on a phrase span, in WORD tokens */
export const MAX_PHRASE_WORDS = 8;

/** A token by position: `tokIdx` indexes `tokenize(line.text)` (whitespace tokens included) — the
 *  same number `Transcript` puts on `data-tok`. */
export interface TokenRef {
  lineId: string;
  tokIdx: number;
}

export type SelectionMode = 'tap' | 'extend' | 'swipe' | 'nudge';

/** The span: from the start token's charStart on the start line to the end token's charEnd on
 *  the end line. `mode` = how it was formed (→ `support.selection` on the lookup). */
export interface PhraseSelection {
  startLineId: string;
  startCharStart: number;
  endLineId: string;
  endCharEnd: number;
  mode: SelectionMode;
}

/** A resolved token target — what `Transcript.onWord`/`onWordTap` hand over. */
export interface TokenTarget {
  line: Line;
  token: Token;
}
/** Functions accept either a resolved target or a positional ref. */
export type Target = TokenTarget | TokenRef;

/** A word token with its place in the document. */
export interface WordRef {
  lineIdx: number;
  line: Line;
  tokIdx: number;
  token: Token;
}

/** The highlight range on one row (same shape as `Transcript`'s `Selection`). */
export interface LineSpan {
  lineId: string;
  charStart: number;
  charEnd: number;
}

// --- internals ---------------------------------------------------------------

function lineIndex(lines: Line[], lineId: string): number {
  for (let i = 0; i < lines.length; i++) if (lines[i]!.id === lineId) return i;
  return -1;
}

/** Word tokens of one line, in order, with positions. */
function wordsOfLine(lines: Line[], lineIdx: number): WordRef[] {
  const line = lines[lineIdx];
  if (!line) return [];
  const out: WordRef[] = [];
  const toks = tokenize(line.text);
  for (let i = 0; i < toks.length; i++) {
    const token = toks[i]!;
    if (token.word !== '') out.push({ lineIdx, line, tokIdx: i, token });
  }
  return out;
}

/** Word tokens of lines [from, to] (inclusive, clamped), in document order. */
function wordsInWindow(lines: Line[], from: number, to: number): WordRef[] {
  const out: WordRef[] = [];
  for (let i = Math.max(0, from); i <= Math.min(lines.length - 1, to); i++)
    out.push(...wordsOfLine(lines, i));
  return out;
}

const cmp = (a: WordRef, b: WordRef): number => a.lineIdx - b.lineIdx || a.tokIdx - b.tokIdx;
const isRef = (t: Target): t is TokenRef => 'tokIdx' in t;

/** Resolve a target to a WordRef (null: unknown line, out of range, or not a word token). */
function toWordRef(t: Target, lines: Line[]): WordRef | null {
  if (isRef(t)) {
    const lineIdx = lineIndex(lines, t.lineId);
    if (lineIdx === -1) return null;
    const line = lines[lineIdx]!;
    const token = tokenize(line.text)[t.tokIdx];
    if (!token || token.word === '') return null;
    return { lineIdx, line, tokIdx: t.tokIdx, token };
  }
  const lineIdx = lineIndex(lines, t.line.id);
  if (lineIdx === -1 || t.token.word === '') return null;
  const toks = tokenize(t.line.text);
  const tokIdx = toks.findIndex(
    (x) => x.charStart === t.token.charStart && x.charEnd === t.token.charEnd,
  );
  if (tokIdx === -1) return null;
  return { lineIdx, line: lines[lineIdx]!, tokIdx, token: toks[tokIdx]! };
}

interface Located {
  start: WordRef;
  end: WordRef;
}

/** The selection's start/end word tokens (null when the ids/offsets don't resolve). Tolerates
 *  offsets that don't sit exactly on a token edge (snaps inward). */
function locate(sel: PhraseSelection, lines: Line[]): Located | null {
  const si = lineIndex(lines, sel.startLineId);
  const ei = lineIndex(lines, sel.endLineId);
  if (si === -1 || ei === -1 || ei < si) return null;
  const sw = wordsOfLine(lines, si);
  const ew = si === ei ? sw : wordsOfLine(lines, ei);
  const start =
    sw.find((w) => w.token.charStart === sel.startCharStart) ??
    sw.find((w) => w.token.charStart >= sel.startCharStart);
  let end: WordRef | undefined = ew.find((w) => w.token.charEnd === sel.endCharEnd);
  if (!end) for (const w of ew) if (w.token.charEnd <= sel.endCharEnd) end = w;
  if (!start || !end || cmp(start, end) > 0) return null;
  return { start, end };
}

/** Build a selection from two word refs, enforcing the rules: start ≤ end, same/adjacent line,
 *  1 ≤ words ≤ MAX. Null when a rule fails. */
function build(a: WordRef, b: WordRef, mode: SelectionMode, lines: Line[]): PhraseSelection | null {
  const [start, end] = cmp(a, b) <= 0 ? [a, b] : [b, a];
  if (end.lineIdx - start.lineIdx > 1) return null;
  const n = wordsBetween(start, end, lines).length;
  if (n < 1 || n > MAX_PHRASE_WORDS) return null;
  return {
    startLineId: start.line.id,
    startCharStart: start.token.charStart,
    endLineId: end.line.id,
    endCharEnd: end.token.charEnd,
    mode,
  };
}

/** Word tokens from `start` to `end` inclusive, in document order. */
function wordsBetween(start: WordRef, end: WordRef, lines: Line[]): WordRef[] {
  return wordsInWindow(lines, start.lineIdx, end.lineIdx).filter(
    (w) => cmp(w, start) >= 0 && cmp(w, end) <= 0,
  );
}

// --- constructors / reducers -------------------------------------------------

/** A single tapped word (mode 'tap'). */
export function selectionFromToken(line: Line, token: Token): PhraseSelection {
  return {
    startLineId: line.id,
    startCharStart: token.charStart,
    endLineId: line.id,
    endCharEnd: token.charEnd,
    mode: 'tap',
  };
}

/**
 * A pre-identified chunk tapped as a unit (M4a): the resolver's `[charStart, charEnd)` on one
 * line becomes a span, so the sheet's ◀ ▶ handles and the highlight work exactly as they do for a
 * hand-made phrase. Mode 'chunk' would be a fifth selection mode; it travels as 'tap' when it is
 * one word and 'extend' when it is several — what `support.selection` records is HOW the span was
 * formed, and a chunk tap is the app offering the span, not the learner building it.
 */
export function selectionFromRange(
  line: Line,
  charStart: number,
  charEnd: number,
): PhraseSelection | null {
  if (!(charEnd > charStart)) return null;
  // The resolver's span covers the WORDS ("ვერ შეასრულა"); a `PhraseSelection`'s edges are RAW
  // token offsets, which include trailing punctuation ("შეასრულა."). Without snapping outward,
  // `locate` finds no token ending exactly there and falls back to the previous word — the chunk
  // card would open on the first word of the chunk instead of the chunk.
  const toks = tokenize(line.text);
  const first = toks.find((t) => t.word !== '' && t.charEnd > charStart);
  const last = [...toks].reverse().find((t) => t.word !== '' && t.charStart < charEnd);
  if (!first || !last || first.charStart > last.charStart) return null;
  return {
    startLineId: line.id,
    startCharStart: first.charStart,
    endLineId: line.id,
    endCharEnd: last.charEnd,
    mode: first.charStart === last.charStart ? 'tap' : 'extend',
  };
}

/**
 * Tap-to-extend: with `sel` open, the learner taps `target` → the span grows to cover it (A…B in
 * document order). Null when the target is not on the same/adjacent line (or would make the span
 * cover more than two lines), when it is already inside the span (nothing to extend — see
 * `resolveTap`), or when the cap would be exceeded. Mode 'extend'.
 */
export function extendSelection(
  sel: PhraseSelection,
  target: Target,
  lines: Line[],
): PhraseSelection | null {
  const loc = locate(sel, lines);
  const t = toWordRef(target, lines);
  if (!loc || !t) return null;
  if (cmp(t, loc.start) >= 0 && cmp(t, loc.end) <= 0) return null; // inside: no change
  const start = cmp(t, loc.start) < 0 ? t : loc.start;
  const end = cmp(t, loc.end) > 0 ? t : loc.end;
  return build(start, end, 'extend', lines);
}

/**
 * ◀ ▶ nudge handles: move one edge by one WORD token (skipping punctuation-only tokens), possibly
 * across into the adjacent line. Never inverts (start ≤ end, so ≥ 1 word), never exceeds the cap or
 * two lines. Null when the move is impossible. Mode 'nudge'.
 *   side 'start', dir -1 = widen left · +1 = narrow from the left
 *   side 'end',   dir +1 = widen right · -1 = narrow from the right
 */
export function nudgeSelection(
  sel: PhraseSelection,
  side: 'start' | 'end',
  dir: -1 | 1,
  lines: Line[],
): PhraseSelection | null {
  const loc = locate(sel, lines);
  if (!loc) return null;
  const words = wordsInWindow(lines, loc.start.lineIdx - 1, loc.end.lineIdx + 1);
  const si = words.findIndex((w) => cmp(w, loc.start) === 0);
  const ei = words.findIndex((w) => cmp(w, loc.end) === 0);
  if (si === -1 || ei === -1) return null;
  if (side === 'start') {
    const ns = si + dir;
    if (ns < 0 || ns > ei) return null;
    return build(words[ns]!, loc.end, 'nudge', lines);
  }
  const ne = ei + dir;
  if (ne < si || ne >= words.length) return null;
  return build(loc.start, words[ne]!, 'nudge', lines);
}

/** Swipe across tokens from `a` to `b` (either order): same rules as extend. Mode 'swipe'. */
export function swipeSelection(a: Target, b: Target, lines: Line[]): PhraseSelection | null {
  const wa = toWordRef(a, lines);
  const wb = toWordRef(b, lines);
  if (!wa || !wb) return null;
  return build(wa, wb, 'swipe', lines);
}

/** What a word tap means while `sel` is open (the integrator's one call for `onWord`):
 *  'extend' with the new span · 'fresh' = treat as a new single-word tap (no selection open, tap
 *  inside the span, or a line out of reach) · 'ignore' = reachable but over the cap. */
export function resolveTap(
  sel: PhraseSelection | null,
  target: Target,
  lines: Line[],
): { kind: 'extend'; selection: PhraseSelection } | { kind: 'fresh' } | { kind: 'ignore' } {
  if (!sel) return { kind: 'fresh' };
  const t = toWordRef(target, lines);
  if (!t) return { kind: 'fresh' };
  if (containsToken(sel, target, lines) || !isReachable(sel, t.line.id, lines))
    return { kind: 'fresh' };
  const ext = extendSelection(sel, target, lines);
  return ext ? { kind: 'extend', selection: ext } : { kind: 'ignore' };
}

// --- queries -----------------------------------------------------------------

/** Could a tap on `lineId` extend `sel` without spanning more than two lines? */
export function isReachable(sel: PhraseSelection, lineId: string, lines: Line[]): boolean {
  const si = lineIndex(lines, sel.startLineId);
  const ei = lineIndex(lines, sel.endLineId);
  const li = lineIndex(lines, lineId);
  if (si === -1 || ei === -1 || li === -1) return false;
  return Math.max(ei, li) - Math.min(si, li) <= 1;
}

/** Is the target word inside the span? */
export function containsToken(sel: PhraseSelection, target: Target, lines: Line[]): boolean {
  const loc = locate(sel, lines);
  const t = toWordRef(target, lines);
  if (!loc || !t) return false;
  return cmp(t, loc.start) >= 0 && cmp(t, loc.end) <= 0;
}

/** Same span (mode ignored). */
export function sameSpan(a: PhraseSelection | null, b: PhraseSelection | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.startLineId === b.startLineId &&
    a.startCharStart === b.startCharStart &&
    a.endLineId === b.endLineId &&
    a.endCharEnd === b.endCharEnd
  );
}

/** Resolve a positional ref (from `Transcript.onSwipe`) to its line + token; null when it isn't a
 *  word token. */
export function resolveTokenRef(ref: TokenRef, lines: Line[]): TokenTarget | null {
  const w = toWordRef(ref, lines);
  return w ? { line: w.line, token: w.token } : null;
}

/** The selected WORD tokens, in order (empty when the selection doesn't resolve). */
export function selectionTokens(sel: PhraseSelection, lines: Line[]): TokenTarget[] {
  const loc = locate(sel, lines);
  if (!loc) return [];
  return wordsBetween(loc.start, loc.end, lines).map((w) => ({ line: w.line, token: w.token }));
}

/** The selected words (trimmed forms), in order. */
export function selectionWords(sel: PhraseSelection, lines: Line[]): string[] {
  return selectionTokens(sel, lines).map((t) => t.token.word);
}

export function countWords(sel: PhraseSelection, lines: Line[]): number {
  return selectionTokens(sel, lines).length;
}

export function isSingleWord(sel: PhraseSelection, lines: Line[]): boolean {
  return countWords(sel, lines) === 1;
}

/**
 * The phrase text as the learner sees it — what goes to `gloss.selection` / `savedCreate.selection`:
 * per line, the line text from the first selected token to the last (inner punctuation and spacing
 * kept verbatim, the outer edges trimmed of punctuation like a single word is), lines joined by one
 * space. For plain words this equals the words joined by single spaces.
 */
export function selectionText(sel: PhraseSelection, lines: Line[]): string {
  const loc = locate(sel, lines);
  if (!loc) return '';
  const parts: string[] = [];
  for (let i = loc.start.lineIdx; i <= loc.end.lineIdx; i++) {
    const ws = wordsOfLine(lines, i).filter((w) => cmp(w, loc.start) >= 0 && cmp(w, loc.end) <= 0);
    if (ws.length === 0) continue;
    const text = lines[i]!.text.slice(ws[0]!.token.charStart, ws[ws.length - 1]!.token.charEnd);
    parts.push(text.replace(WORD_TRIM, ''));
  }
  return parts.join(' ');
}

/** The sentence for the gloss: the line texts the span touches, joined by one space (a single-line
 *  span → that line's text unchanged). */
export function selectionSentence(sel: PhraseSelection, lines: Line[]): string {
  const si = lineIndex(lines, sel.startLineId);
  const ei = lineIndex(lines, sel.endLineId);
  if (si === -1 || ei === -1 || ei < si) return '';
  return lines
    .slice(si, ei + 1)
    .map((l) => l.text)
    .join(' ');
}

/** Char offsets of the span inside `selectionSentence(sel, lines)` — `SheetBase.sel` for the
 *  sheet's Highlight (start token's charStart … end token's charEnd in the joined string). */
export function selectionOffsets(
  sel: PhraseSelection,
  lines: Line[],
): { charStart: number; charEnd: number } | null {
  const loc = locate(sel, lines);
  if (!loc) return null;
  let endBase = 0;
  for (let i = loc.start.lineIdx; i < loc.end.lineIdx; i++) endBase += lines[i]!.text.length + 1;
  return { charStart: loc.start.token.charStart, charEnd: endBase + loc.end.token.charEnd };
}

/**
 * The highlight range on every row the span touches: start line = from the start token through
 * the end of the line, end line = from 0 to the end token, a middle line = whole line, a single
 * line = start…end. Written generally although the adjacent-only rule means at most two rows.
 */
export function lineSpans(sel: PhraseSelection, lines: Line[]): LineSpan[] {
  const loc = locate(sel, lines);
  if (!loc) return [];
  const out: LineSpan[] = [];
  for (let i = loc.start.lineIdx; i <= loc.end.lineIdx; i++) {
    const line = lines[i]!;
    out.push({
      lineId: line.id,
      charStart: i === loc.start.lineIdx ? loc.start.token.charStart : 0,
      charEnd: i === loc.end.lineIdx ? loc.end.token.charEnd : line.text.length,
    });
  }
  return out;
}

/** `lineSpans` for one row (null when the row is outside the span). */
export function lineSpan(
  sel: PhraseSelection,
  lineId: string,
  lines: Line[],
): { charStart: number; charEnd: number } | null {
  const s = lineSpans(sel, lines).find((x) => x.lineId === lineId);
  return s ? { charStart: s.charStart, charEnd: s.charEnd } : null;
}
